<template>
  <div class="tig-postnl-field-group postnl-delivery-options" v-if="isPostnlShippingSelected">
    <div class="postnl-delivery-options__toggle field__group">
      <label :class="{_active: postnlMode === 'delivery'}">
        <input
          type="radio"
          name="postnl-mode"
          value="delivery"
          :checked="postnlMode === 'delivery'"
          @change="setMode('delivery')"
        />
        {{ $t('Home delivery') }}
      </label>
      <label :class="{_active: postnlMode === 'pickup'}">
        <input
          type="radio"
          name="postnl-mode"
          value="pickup"
          :checked="postnlMode === 'pickup'"
          @change="setMode('pickup')"
        />
        {{ $t('Pick up at a PostNL point') }}
      </label>
    </div>

    <p class="field-error" v-if="postnlError && !isLoading">{{ $t(postnlError) }}</p>

    <template v-if="postnlMode === 'delivery'">
      <h3>{{ $t('Choose a delivery date') }}</h3>
      <div class="_loading" v-show="loadingPostnlTimeframes"></div>
      <div class="postnl-delivery-options__days" v-if="!loadingPostnlTimeframes && groupedTimeframes.length">
        <section
          class="postnl-delivery-options__day"
          v-for="(group, groupIndex) in groupedTimeframes"
          :key="group.date || groupIndex"
        >
          <h4>{{ group.label }}</h4>
          <ul class="postnl-delivery-options__list">
            <li v-for="(frame, index) in group.frames" :key="`${frame.date}-${frame.from}-${index}`">
              <label>
                <input
                  type="radio"
                  name="postnl-delivery-date"
                  :value="frame.date"
                  :checked="isSelected(frame)"
                  @change="selectFrame(frame)"
                />
                <span>
                  <span v-if="frame.from_friendly && frame.to_friendly">
                    {{ frame.from_friendly }} - {{ frame.to_friendly }}
                  </span>
                  <span v-if="optionLabel(frame)"> {{ optionLabel(frame) }}</span>
                  <span v-if="frame.fallback">{{ frame.fallback }}</span>
                </span>
              </label>
            </li>
          </ul>
        </section>
      </div>
    </template>

    <template v-else>
      <h3>{{ $t('Choose a pickup point') }}</h3>
      <div class="_loading" v-show="loadingPostnlLocations"></div>
      <p v-if="!loadingPostnlLocations && pickupDate" class="postnl-delivery-options__pickup-date">
        {{ $t('Ready for pickup from') }}: <strong>{{ pickupDateFriendly }}</strong>
      </p>
      <ul class="postnl-delivery-options__list postnl-pickup-locations" v-if="!loadingPostnlLocations && pickupLocations.length">
        <li v-for="location in pickupLocations" :key="location.LocationCode">
          <label>
            <input
              type="radio"
              name="postnl-pickup-location"
              :value="location.LocationCode"
              :checked="isLocationSelected(location)"
              @change="selectLocation(location)"
            />
            <span>
              <strong>{{ location.Name }}</strong>
              <span v-if="locationDistance(location)" class="postnl-pickup-locations__distance">{{ locationDistance(location) }}</span>
              <br/>
              {{ locationAddress(location).Street }} {{ locationAddress(location).HouseNr }}{{ locationAddress(location).HouseNrExt || '' }}<br/>
              {{ locationAddress(location).Zipcode }} {{ locationAddress(location).City }}
              <span v-if="todayOpeningHours(location)" class="postnl-pickup-locations__hours">
                {{ $t('Open today') }}: {{ todayOpeningHours(location) }}
              </span>
              <details v-if="weeklyOpeningHours(location).length" class="postnl-pickup-locations__week">
                <summary>{{ $t('Opening hours') }}</summary>
                <ul>
                  <li v-for="row in weeklyOpeningHours(location)" :key="row.day">
                    <span>{{ row.day }}</span>
                    <span>{{ row.hours }}</span>
                  </li>
                </ul>
              </details>
            </span>
          </label>
        </li>
      </ul>
      <p v-if="!loadingPostnlLocations && !pickupLocations.length && !postnlError" class="postnl-delivery-options__empty">
        {{ $t('No pickup points found for this address.') }}
      </p>
    </template>
  </div>
</template>

<script lang="ts">
  import {Component, Vue, Watch} from 'vue-property-decorator';

  @Component({
    name: 'checkout-shipping-postnl-delivery-options'
  })

  export default class extends Vue {
    private get isPostnlShippingSelected(): boolean {
      return this.$store.getters['CheckoutShipping/isPostnlShippingSelected'];
    }

    private get postnlMode(): string {
      return this.$store.getters['CheckoutShipping/postnlMode'];
    }

    private get loadingPostnlTimeframes(): boolean {
      return this.$store.getters['CheckoutShipping/loadingPostnlTimeframes'];
    }

    private get loadingPostnlLocations(): boolean {
      return this.$store.getters['CheckoutShipping/loadingPostnlLocations'];
    }

    private get isLoading(): boolean {
      return this.loadingPostnlTimeframes || this.loadingPostnlLocations;
    }

    private get postnlError(): string | null {
      return this.$store.getters['CheckoutShipping/postnlError'];
    }

    private get selectedOption(): any {
      return this.$store.getters['CheckoutShipping/postnlDeliveryOption'];
    }

    private get groupedTimeframes(): Array<{date: string, label: string, frames: Array<any>}> {
      const groups = this.$store.getters['CheckoutShipping/postnlTimeframes'] || [];
      const nested = Array.isArray(groups) && groups.some((group) => Array.isArray(group));
      const buckets = nested ? groups : this.groupFlatFrames(groups);
      const weekdayFormatter = new Intl.DateTimeFormat('nl-NL', {weekday: 'short', day: 'numeric', month: 'long'});

      return buckets
        .map((frames) => (Array.isArray(frames) ? frames : [frames]).filter((frame) => frame && (frame.date || frame.fallback)))
        .filter((frames) => frames.length)
        .map((frames) => {
          const date = frames[0].date || '';
          const parsed = this.parsePostnlDate(date);
          const label = parsed
            ? weekdayFormatter.format(parsed)
            : (frames[0].day || date);

          return {date, label, frames};
        });
    }

    @Watch('isPostnlShippingSelected')
    onPostnlShippingSelected(selected: boolean): void {
      if (selected) {
        this.ensurePostnlDataLoaded();
      }
    }

    @Watch('postnlMode')
    onPostnlModeChange(): void {
      this.ensurePostnlDataLoaded();
    }

    private mounted(): void {
      this.ensurePostnlDataLoaded();
    }

    /** After refresh, pickup mode is restored from session but locations are not. */
    private ensurePostnlDataLoaded(): void {
      if (!this.isPostnlShippingSelected || this.isLoading) {
        return;
      }

      if (this.postnlMode === 'pickup' && !this.pickupLocations.length) {
        this.$store.dispatch('CheckoutShipping/collectPostnlPickupLocations');
      }
    }

    // TIG returns dates as dd-mm-yyyy, which Date() cannot parse.
    private parsePostnlDate(date: string): Date | null {
      const match = (date || '').match(/^(\d{2})-(\d{2})-(\d{4})$/);
      if (match) {
        return new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]));
      }

      const parsed = new Date(date);
      return isNaN(parsed.getTime()) ? null : parsed;
    }

    private groupFlatFrames(frames: Array<any>): Array<Array<any>> {
      const byDate = new Map<string, Array<any>>();
      (frames || []).forEach((frame) => {
        if (!frame) {
          return;
        }
        const key = frame.date || 'fallback';
        if (!byDate.has(key)) {
          byDate.set(key, []);
        }
        byDate.get(key).push(frame);
      });

      return Array.from(byDate.values());
    }

    private get eveningDeliveryFee(): number {
      return Number(this.$store.getters['CheckoutShipping/postnlEveningFee'] || 0);
    }

    private optionLabel(frame): string {
      const option = frame?.option;
      if (!option || option === 'Daytime') {
        return '';
      }

      const label = String(this.$t(option));
      if (option === 'Evening' && this.eveningDeliveryFee > 0) {
        return `(${label} + €${this.eveningDeliveryFee.toFixed(2).replace('.', ',')})`;
      }

      return `(${label})`;
    }

    private get pickupLocations(): Array<any> {
      return this.$store.getters['CheckoutShipping/postnlPickupLocations'] || [];
    }

    private get pickupDate(): string | null {
      return this.$store.getters['CheckoutShipping/postnlPickupDate'];
    }

    private get pickupDateFriendly(): string {
      if (!this.pickupDate) {
        return '';
      }
      const parsed = this.parsePostnlDate(this.pickupDate);
      return parsed
        ? parsed.toLocaleDateString('nl-NL', {weekday: 'long', day: 'numeric', month: 'long'})
        : this.pickupDate;
    }

    private setMode(mode: string): void {
      this.$store.dispatch('CheckoutShipping/setPostnlMode', mode);
    }

    private isSelected(frame): boolean {
      return this.selectedOption?.type === 'delivery'
        && this.selectedOption?.date === frame.date
        && this.selectedOption?.from === frame.from
        && this.selectedOption?.option === (frame.option || this.selectedOption?.option);
    }

    private isLocationSelected(location): boolean {
      return this.selectedOption?.type === 'pickup'
        && this.selectedOption?.LocationCode === location.LocationCode;
    }

    private locationAddress(location): any {
      return location?.Address || location || {};
    }

    private locationDistance(location): string {
      const meters = Number(location?.Distance ?? location?.distance ?? NaN);
      if (!Number.isFinite(meters) || meters <= 0) {
        return '';
      }

      const km = meters / 1000;
      return `${km.toLocaleString('nl-NL', {minimumFractionDigits: km < 10 ? 1 : 0, maximumFractionDigits: 1})} km`;
    }

    private openingHoursMap(location): Record<string, string> {
      const hours = location?.OpeningHours || location?.openingHours || {};
      if (typeof hours === 'string' && hours.trim() !== '') {
        return {today: hours};
      }

      if (!hours || typeof hours !== 'object') {
        return {};
      }

      // TIG shape: { Monday: { string: ['10:00-18:00'] }, ... }
      const mapped: Record<string, string> = {};
      Object.keys(hours).forEach((day) => {
        const value = hours[day];
        const ranges = Array.isArray(value)
          ? value
          : (Array.isArray(value?.string) ? value.string : (value ? [value] : []));
        const text = ranges.filter(Boolean).map((range) => String(range)).join(', ');
        if (text) {
          mapped[day] = text;
        }
      });

      return mapped;
    }

    private todayOpeningHours(location): string {
      const hours = this.openingHoursMap(location);
      const today = new Date().toLocaleDateString('en-US', {weekday: 'long'});
      return hours[today] || hours[today.toLowerCase()] || hours.today || '';
    }

    private weeklyOpeningHours(location): Array<{day: string, hours: string}> {
      const hours = this.openingHoursMap(location);
      const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

      return days
        .map((day) => ({
          day: String(this.$t(day)),
          hours: hours[day] || hours[day.toLowerCase()] || '',
        }))
        .filter((row) => row.hours);
    }

    private getCustomerAddress(): any {
      const address = this.$store.getters['CheckoutAddress/getAddressData']?.[1] || {};
      const street = Array.isArray(address.street) ? address.street : ['', '', ''];
      return {
        firstname: address.firstname || '',
        lastname: address.lastname || '',
        telephone: address.telephone || '',
        country: address.country_id || address.country || 'NL',
        postcode: (address.postcode || '').replace(/\s+/g, ''),
        street: street[0] || '',
        housenumber: street[1] || '',
        city: address.city || '',
      };
    }

    private selectFrame(frame): void {
      const address = this.getCustomerAddress();

      this.$store.commit('CheckoutShipping/SET_POSTNL_ERROR', null);
      this.$store.dispatch('CheckoutShipping/setPostnlDeliveryOption', {
        type: 'delivery',
        date: frame.date,
        option: frame.option || 'Daytime',
        from: frame.from,
        to: frame.to,
        country: address.country,
        address,
      });
    }

    private selectLocation(location): void {
      const customerData = this.getCustomerAddress();
      const address = this.locationAddress(location);

      this.$store.commit('CheckoutShipping/SET_POSTNL_ERROR', null);
      this.$store.dispatch('CheckoutShipping/setPostnlDeliveryOption', {
        type: 'pickup',
        date: this.pickupDate,
        country: address.Countrycode || 'NL',
        LocationCode: location.LocationCode,
        RetailNetworkID: location.RetailNetworkID || '',
        OpeningHours: location.OpeningHours || '',
        name: location.Name,
        address: {
          Name: location.Name,
          Street: address.Street,
          HouseNr: address.HouseNr,
          HouseNrExt: address.HouseNrExt || '',
          Zipcode: address.Zipcode,
          City: address.City,
          Countrycode: address.Countrycode || 'NL',
        },
        customerData,
      });
    }
  }
</script>
